import type { IconButtonProps, SpanProps } from "@chakra-ui/react"
import { 
  IconButton, 
  Span,
  Skeleton,
  ClientOnly
} from "@chakra-ui/react"
import * as React from "react"
import { LuMoon, LuSun } from "react-icons/lu"

export type ColorMode = "light" | "dark" | "system"

export interface UseColorModeReturn {
  colorMode: ColorMode
  setColorMode: (colorMode: ColorMode) => void
  toggleColorMode: () => void
  resolvedColorMode: "light" | "dark"
}

function getSystemPreference(): "light" | "dark" {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

function resolveColorMode(colorMode: ColorMode): "light" | "dark" {
  if (colorMode === 'system') {
    return getSystemPreference()
  }
  return colorMode
}

// Create color mode context
const ColorModeContext = React.createContext<UseColorModeReturn | undefined>(undefined)

export function ColorModeProvider({ children }: { children: React.ReactNode }) {
  const [colorMode, setColorModeState] = React.useState<ColorMode>("system")
  const [resolvedColorMode, setResolvedColorMode] = React.useState<"light" | "dark">("light")
  
  const applyColorMode = React.useCallback((resolved: "light" | "dark") => {
    setResolvedColorMode(resolved)
    document.documentElement.setAttribute('data-theme', resolved)
    document.documentElement.style.colorScheme = resolved
    
    // Apply background and text colors to body
    if (resolved === 'dark') {
      document.body.style.backgroundColor = '#111827'
      document.body.style.color = '#f9fafb'
    } else {
      document.body.style.backgroundColor = '#ffffff'
      document.body.style.color = '#111827'
    }
  }, [])
  
  // Handle system preference changes
  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      if (colorMode === 'system') {
        const newResolved = getSystemPreference()
        applyColorMode(newResolved)
      }
    }
    
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [colorMode, applyColorMode])
  
  // Initialize color mode on mount
  React.useEffect(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('chakra-ui-color-mode') as ColorMode
    const initialMode = (saved === 'dark' || saved === 'light' || saved === 'system') ? saved : 'system'
    
    setColorModeState(initialMode)
    const resolved = resolveColorMode(initialMode)
    applyColorMode(resolved)
  }, [applyColorMode])
  
  // Update resolved color mode when colorMode changes
  React.useEffect(() => {
    const resolved = resolveColorMode(colorMode)
    applyColorMode(resolved)
  }, [colorMode, applyColorMode])
  
  const setColorMode = React.useCallback((mode: ColorMode) => {
    setColorModeState(mode)
    localStorage.setItem('chakra-ui-color-mode', mode)
  }, [])
  
  const toggleColorMode = React.useCallback(() => {
    if (colorMode === 'system') {
      // If currently system, switch to the opposite of current resolved mode
      const newMode = resolvedColorMode === 'light' ? 'dark' : 'light'
      setColorMode(newMode)
    } else if (colorMode === 'light') {
      setColorMode('dark')
    } else {
      setColorMode('system')
    }
  }, [colorMode, resolvedColorMode, setColorMode])
  
  const value = React.useMemo(() => ({
    colorMode,
    setColorMode,
    toggleColorMode,
    resolvedColorMode,
  }), [colorMode, setColorMode, toggleColorMode, resolvedColorMode])
  
  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  )
}

export function useColorMode(): UseColorModeReturn {
  const context = React.useContext(ColorModeContext)
  if (context === undefined) {
    throw new Error('useColorMode must be used within a ColorModeProvider')
  }
  return context
}

export function useColorModeValue<T>(light: T, dark: T) {
  const { resolvedColorMode } = useColorMode()
  return resolvedColorMode === "dark" ? dark : light
}

export function ColorModeIcon() {
  const { colorMode, resolvedColorMode } = useColorMode()
  
  if (colorMode === 'system') {
    return resolvedColorMode === 'dark' ? <LuMoon /> : <LuSun />
  }
  return colorMode === "dark" ? <LuMoon /> : <LuSun />
}

interface ColorModeButtonProps extends Omit<IconButtonProps, "aria-label"> {}

export const ColorModeButton = React.forwardRef<
  HTMLButtonElement,
  ColorModeButtonProps
>(function ColorModeButton(props, ref) {
  const { toggleColorMode, colorMode } = useColorMode()
  
  const getAriaLabel = () => {
    if (colorMode === 'system') return 'Switch to dark mode'
    if (colorMode === 'light') return 'Switch to dark mode'
    return 'Switch to system preference'
  }
  
  return (
    <ClientOnly fallback={<Skeleton boxSize="8" />}>
      <IconButton
        onClick={toggleColorMode}
        variant="ghost"
        aria-label={getAriaLabel()}
        size="sm"
        ref={ref}
        {...props}
        css={{
          _icon: {
            width: "5",
            height: "5",
          },
        }}
      >
        <ColorModeIcon />
      </IconButton>
    </ClientOnly>
  )
})

export const LightMode = React.forwardRef<HTMLSpanElement, SpanProps>(
  function LightMode(props, ref) {
    return (
      <Span
        color="fg"
        display="contents"
        className="chakra-theme light"
        colorPalette="gray"
        colorScheme="light"
        ref={ref}
        {...props}
      />
    )
  },
)

export const DarkMode = React.forwardRef<HTMLSpanElement, SpanProps>(
  function DarkMode(props, ref) {
    return (
      <Span
        color="fg"
        display="contents"
        className="chakra-theme dark"
        colorPalette="gray"
        colorScheme="dark"
        ref={ref}
        {...props}
      />
    )
  },
)
